import React, { useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Upload, Loader2, ChevronLeft, Check, AlertTriangle, XCircle, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TARGET_FIELDS = [
  { f: 'activityId', l: 'Activity ID (external)' }, { f: 'activityCode', l: 'Activity Code' },
  { f: 'activityName', l: 'Activity Name *' }, { f: 'wbsCode', l: 'WBS Code' }, { f: 'wbsName', l: 'WBS Name' },
  { f: 'plannedStart', l: 'Planned Start' }, { f: 'plannedFinish', l: 'Planned Finish' },
  { f: 'actualStart', l: 'Actual Start' }, { f: 'actualFinish', l: 'Actual Finish' },
  { f: 'originalDuration', l: 'Original Duration' }, { f: 'remainingDuration', l: 'Remaining Duration' },
  { f: 'reportedPercent', l: 'Reported Progress %' }, { f: 'status', l: 'Status' },
  { f: 'buildingId', l: 'Building (id)' }, { f: 'floorId', l: 'Floor (id)' }, { f: 'zoneId', l: 'Zone (id)' },
];

const ScheduleImport = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [sheet, setSheet] = useState('');
  const [mapping, setMapping] = useState({});
  const [validation, setValidation] = useState(null);
  const [meta, setMeta] = useState({ name: '', revision: 'Baseline', version: '1', dataDate: '' });
  const [busy, setBusy] = useState(false);

  const autoMap = (columns) => {
    const m = {};
    const lc = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    columns.forEach((c) => {
      const k = lc(c);
      if (['activityid', 'id', 'taskid'].includes(k)) m.activityId = c;
      else if (['activitycode', 'code', 'wbscode2', 'taskcode'].includes(k)) m.activityCode = c;
      else if (['activityname', 'activity', 'taskname', 'name', 'description', 'task'].includes(k)) m.activityName = c;
      else if (['wbs', 'wbscode'].includes(k)) m.wbsCode = c;
      else if (['wbsname'].includes(k)) m.wbsName = c;
      else if (['plannedstart', 'start', 'startdate', 'baselinestart'].includes(k)) m.plannedStart = c;
      else if (['plannedfinish', 'finish', 'end', 'enddate', 'finishdate', 'baselinefinish'].includes(k)) m.plannedFinish = c;
      else if (['actualstart'].includes(k)) m.actualStart = c;
      else if (['actualfinish'].includes(k)) m.actualFinish = c;
      else if (['originalduration', 'duration', 'origdur'].includes(k)) m.originalDuration = c;
      else if (['remainingduration', 'remdur'].includes(k)) m.remainingDuration = c;
      else if (['percentcomplete', 'complete', 'progress', 'reportedprogress', 'percent'].includes(k)) m.reportedPercent = c;
      else if (['status', 'activitystatus'].includes(k)) m.status = c;
    });
    return m;
  };

  const doParse = async (f, sheetName) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      if (sheetName) fd.append('sheet', sheetName);
      const d = await axios.post(`${API}/projects/${projectId}/schedule-import/parse`, fd).then((r) => r.data);
      setParsed(d); setSheet(d.sheet || '');
      setMapping((prev) => (Object.keys(prev).length ? prev : autoMap(d.columns)));
      if (!meta.name) setMeta((m) => ({ ...m, name: f.name.replace(/\.[^.]+$/, '') }));
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || 'Parse failed'); } finally { setBusy(false); }
  };

  const doValidate = async () => {
    setBusy(true);
    try {
      const d = await axios.post(`${API}/projects/${projectId}/schedule-import/validate`, { mapping, rows: parsed.rows }).then((r) => r.data);
      setValidation(d); setStep(3);
    } catch (e) { toast.error('Validation failed'); } finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true);
    try {
      const d = await axios.post(`${API}/projects/${projectId}/schedule-import/commit`, {
        scheduleMeta: { name: meta.name || 'Imported Schedule', revision: meta.revision, version: meta.version, dataDate: meta.dataDate || null, sourceFile: file?.name },
        mapping, rows: parsed.rows,
      }).then((r) => r.data);
      toast.success(`Imported ${d.created} activities (${d.skipped} skipped). Activate it from the schedule to make it live.`);
      navigate(`/app/projects/${projectId}/schedule`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        <button onClick={() => navigate(`/app/projects/${projectId}/schedule`)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="schedule-import-back"><ChevronLeft className="w-4 h-4" /> Schedule Intelligence</button>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="w-7 h-7 text-blue-600" /> Import Schedule</h1>
        <p className="text-sm text-slate-500 -mt-2">CSV or XLSX (e.g. a Primavera / MS Project export). A native P6 XER parser is a future adapter.</p>

        <div className="flex items-center gap-2 text-sm">
          {['Upload', 'Map Columns', 'Validate & Confirm'].map((s, i) => (
            <span key={s} className={`px-3 py-1 rounded-full ${step === i + 1 ? 'bg-blue-600 text-white' : step > i + 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`schedule-import-step-${i + 1}`}>{i + 1}. {s}</span>
          ))}
        </div>

        {step === 1 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="schedule-import-upload">
            <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} className="mb-4" data-testid="schedule-import-file-input" />
            <p className="text-xs text-slate-400 mb-4">Malformed rows are never silently imported.</p>
            <button disabled={!file || busy} onClick={() => doParse(file)} className="px-5 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50" data-testid="schedule-import-parse-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Parse File'}</button>
          </div>
        )}

        {step === 2 && parsed && (
          <div className="space-y-4" data-testid="schedule-import-mapping">
            {parsed.sheets?.length > 1 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <label className="text-sm text-slate-600 mr-2">Sheet</label>
                <select value={sheet} onChange={(e) => { setSheet(e.target.value); doParse(file, e.target.value); }} className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="schedule-import-sheet-select">
                  {parsed.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                <input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} placeholder="Schedule name" className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="schedule-import-name" />
                <input value={meta.revision} onChange={(e) => setMeta({ ...meta, revision: e.target.value })} placeholder="Revision" className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="schedule-import-revision" />
                <input value={meta.version} onChange={(e) => setMeta({ ...meta, version: e.target.value })} placeholder="Version" className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="schedule-import-version" />
                <div><label className="text-[11px] text-slate-400">Data Date</label><input type="date" value={meta.dataDate} onChange={(e) => setMeta({ ...meta, dataDate: e.target.value })} className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="schedule-import-datadate" /></div>
              </div>
              <p className="text-sm text-slate-600 mb-3">Map your columns (fields marked * are required):</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TARGET_FIELDS.map((tf) => (
                  <div key={tf.f} className="flex items-center gap-2">
                    <label className="text-sm text-slate-600 w-40">{tf.l}</label>
                    <select value={mapping[tf.f] || ''} onChange={(e) => setMapping({ ...mapping, [tf.f]: e.target.value })} className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm" data-testid={`schedule-map-${tf.f}`}>
                      <option value="">— none —</option>
                      {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">{parsed.rows.length} data rows detected.</p>
              <button disabled={busy} onClick={doValidate} className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50" data-testid="schedule-import-validate-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validate'}</button>
            </div>
          </div>
        )}

        {step === 3 && validation && (
          <div className="space-y-4" data-testid="schedule-import-validation">
            {validation.fatal && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{validation.fatal}</div>}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-emerald-600" data-testid="schedule-import-valid-count">{validation.validCount}</p><p className="text-xs text-slate-500">Valid rows</p></div>
              <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-red-500">{validation.errors?.length || 0}</p><p className="text-xs text-slate-500">Errors (skipped)</p></div>
              <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-amber-500">{validation.warnings?.length || 0}</p><p className="text-xs text-slate-500">Warnings</p></div>
            </div>
            {(validation.errors || []).length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 max-h-48 overflow-auto" data-testid="schedule-import-errors">
                {validation.errors.map((e) => <div key={e.row} className="text-sm text-red-600 flex items-center gap-2"><XCircle className="w-3 h-3" /> Row {e.row}: {e.messages.join('; ')}</div>)}
              </div>
            )}
            {(validation.warnings || []).length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 max-h-40 overflow-auto" data-testid="schedule-import-warnings">
                {validation.warnings.map((w) => <div key={w.row} className="text-sm text-amber-600 flex items-center gap-2"><AlertTriangle className="w-3 h-3" /> Row {w.row}: {w.messages.join('; ')}</div>)}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button onClick={() => setStep(2)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Back to mapping</button>
              <button disabled={busy || validation.validCount === 0} onClick={doCommit} className="px-5 py-2 bg-emerald-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="schedule-import-commit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Import {validation.validCount} activities</>}</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ScheduleImport;
